import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { useTeamLeaderboard } from '@/hooks/useTeamLeaderboard';
import type { AnalyticsFilters } from '@/hooks/useAnalyticsFilters';

interface AnalyticsLeaderboardProps {
  workspaceId: string;
  filters: AnalyticsFilters;
  role: string;
  tz: string;
  fromDate: Date;
  toDate: Date;
  onRowClick?: (userId: string) => void;
}

export function AnalyticsLeaderboard(props: AnalyticsLeaderboardProps) {
  const [showAll, setShowAll] = useState(false);

  const { rows, isLoading, error, hasMore, loadMore } = useTeamLeaderboard({
    workspaceId: props.workspaceId,
    from: props.fromDate,
    to: props.toDate,
    tz: props.tz,
    role: props.role,
    limit: showAll ? 50 : 10,
  });

  if (isLoading && rows.length === 0) {
    return (
      <Card className="p-4">
        <h2 className="text-sm font-medium mb-3">Team</h2>
        <Skeleton className="h-48 w-full" />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-4">
        <h2 className="text-sm font-medium mb-3">Team</h2>
        <div className="flex flex-col items-center justify-center h-48 gap-2">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <p className="text-sm text-muted-foreground">Couldn't load leaderboard</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <h2 className="text-sm font-medium mb-3">Team</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-muted-foreground text-left">
              <th className="py-2 pr-4 font-medium">User</th>
              <th className="py-2 pr-4 font-medium tabular-nums">Calls</th>
              <th className="py-2 pr-4 font-medium tabular-nums">Meaningful %</th>
              <th className="py-2 pr-4 font-medium tabular-nums">Personal Emails</th>
              <th className="py-2 pr-4 font-medium tabular-nums">New Contacts</th>
              <th className="py-2 font-medium tabular-nums">New Connections</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.user_id}
                className={
                  props.onRowClick
                    ? 'border-b last:border-0 hover:bg-muted/50 cursor-pointer'
                    : 'border-b last:border-0'
                }
                onClick={props.onRowClick ? () => props.onRowClick!(r.user_id) : undefined}
              >
                <td className="py-2 pr-4">{r.display_name}</td>
                <td className="py-2 pr-4 tabular-nums">{r.calls}</td>
                <td className="py-2 pr-4 tabular-nums">{r.meaningful_pct.toFixed(1)}%</td>
                <td className="py-2 pr-4 tabular-nums">{r.personal_emails}</td>
                <td className="py-2 pr-4 tabular-nums">{r.new_contacts}</td>
                <td className="py-2 tabular-nums">{r.new_connections}</td>
              </tr>
            ))}
            {rows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} className="py-4 text-center text-muted-foreground">
                  No team activity yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3">
        {!showAll && rows.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => setShowAll(true)}>
            Show all
          </Button>
        )}
        {showAll && hasMore && (
          <Button variant="outline" size="sm" onClick={loadMore} disabled={isLoading}>
            {isLoading ? 'Loading…' : 'Next 50'}
          </Button>
        )}
        <div />
      </div>
    </Card>
  );
}
